import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Our Work', href: '#work' },
  { label: 'AI Advantage', href: '#ai' },
  { label: 'Results', href: '#results' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-ds-bg/95 border-b border-ds-border' : 'bg-transparent'
      }`}
      style={{ backdropFilter: scrolled ? 'blur(12px)' : 'none' }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="/" className="flex items-center cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/cb16f62b-b978-4a18-b0d4-78b46c2aa554/9ae0cf67-cf30-4444-9d14-012a7830e862_Darkside-Digital-logo-design.png?v=7acdaa383d113350b9c301867b0d13f1"
            alt="Darkside Digital"
            className="h-9 w-auto object-contain"
            width="180"
            height="36"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className="text-white/70 hover:text-white font-body text-sm tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/service-areas"
            className="text-white/70 hover:text-white font-body text-sm tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Service Areas
          </Link>
          <Link
            to="/tourism-website-design"
            className="text-white/70 hover:text-white font-body text-sm tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Tourism
          </Link>
          <Link
            to="/geo-aeo"
            className="text-ds-red hover:text-white font-body text-sm tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ds-red animate-pulse inline-block" />
            GEO / AEO
          </Link>
        </div>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:1300029054"
            className="flex items-center gap-2 text-white/60 hover:text-white font-body text-sm transition-colors duration-200 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-ds-red text-sm" />
            </div>
            1300 029 054
          </a>
          <button
            onClick={() => handleNav('#contact')}
            className="btn-red text-xs"
          >
            Free AI Visibility Audit
          </button>
        </div>

        {/* Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden flex flex-col gap-1.5 cursor-pointer p-1"
          aria-label="Toggle menu"
        >
          <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
          <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-96' : 'max-h-0'}`}
        style={{ background: '#0A0A0A', borderBottom: mobileOpen ? '1px solid #222' : 'none' }}
      >
        <div className="flex flex-col px-6 py-4 gap-4">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className="text-white/70 hover:text-ds-red font-body text-sm tracking-widest uppercase text-left transition-colors duration-200 cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/service-areas"
            onClick={() => setMobileOpen(false)}
            className="text-white/70 hover:text-white font-body text-sm tracking-widest uppercase text-left transition-colors duration-200 cursor-pointer"
          >
            Service Areas
          </Link>
          <Link
            to="/tourism-website-design"
            onClick={() => setMobileOpen(false)}
            className="text-white/70 hover:text-white font-body text-sm tracking-widest uppercase text-left transition-colors duration-200 cursor-pointer"
          >
            Tourism
          </Link>
          <Link
            to="/geo-aeo"
            onClick={() => setMobileOpen(false)}
            className="text-ds-red font-body text-sm tracking-widest uppercase text-left transition-colors duration-200 cursor-pointer flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ds-red animate-pulse" />
            GEO / AEO
          </Link>
          <button
            onClick={() => handleNav('#contact')}
            className="btn-red w-fit mt-2"
          >
            Free AI Visibility Audit
          </button>
          <a
            href="tel:1300029054"
            className="flex items-center gap-2 text-white/60 hover:text-white font-body text-sm transition-colors duration-200 cursor-pointer pb-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-ds-red text-sm" />
            </div>
            1300 029 054
          </a>
        </div>
      </div>
    </nav>
  );
}
